import moment from 'moment'
import { isNumber } from 'lodash'

import config from '@/config'
import lang from '@/lang'

import { transactionTypes } from '@/modules/base/util/constants'
import { FINANCE_API } from '@/modules/banking/constants'
import { getCustomAmountHeaderTemplate } from '@/modules/_helpers/getCustomAmountHeaderTemplate'
import { ListDefaultSettings } from '@/modules/base/components/list/constants'
import { dateFieldComparator, dateFieldFilterComparator } from '@/modules/base/components/list/comparator'

const { dateFormat: { client: clientDateFormat } } = config

const {
  TRANSACTION_TYPE_CASH_INVOICE,
  TRANSACTION_TYPE_CASH_CANCELLATION,
  TRANSACTION_TYPE_CASH_CREDIT_NOTE,
  TRANSACTION_TYPE_INVOICE,
  TRANSACTION_TYPE_CANCELLATION,
  TRANSACTION_TYPE_CREDIT_NOTE,
  TRANSACTION_TYPE_SHOPIFY_CASH_INVOICE,
  TRANSACTION_TYPE_EXPENSE,
  TRANSACTION_TYPE_REVENUE,
  TRANSACTION_TYPE_RECURRING_INVOICE,
  TRANSACTION_TYPE_CLOSING_INVOICE,
  TRANSACTION_TYPE_DEPOSIT_INVOICE,
  TRANSACTION_TYPE_MANUAL_CREDIT_NOTE,
  TRANSACTION_TYPE_PARTIAL_INVOICE,
} = transactionTypes

const { CASH_JOURNAL, CASH_JOURNAL_POS } = FINANCE_API.ACCOUNT_TYPE
const urlPrefix = `${config.resourceHost}banking`

const bankingServers = {
  // local: 'https://psd2banking-api-test.buhl.de',
  // development: 'https://psd2banking-api-test.buhl.de',
  local: 'https://psd2banking-api-prelive.buhl.de',
  // eslint-disable-next-line no-template-curly-in-string
  development: '${PSD2_URL}',
  test: 'https://psd2banking-api-prelive.buhl.de',
  prelive: 'https://psd2banking-api-prelive.buhl.de',
  production: 'https://psd2banking-api.buhl.de',
}

export const urls = {
  bankingApiHost: bankingServers[ENV.releaseStage],
  resource: urlPrefix,
  startBankAccountSetup: `${urlPrefix}/bankaccount/create`,
  updateBankAccountSetup: (contactId) => `${urlPrefix}/bankaccount/${contactId}/create`,
  proceedBankAccountSetupStep: `${urlPrefix}/bankaccount/create/step`,
  completeBankAccountSetup: `${urlPrefix}/bankaccount/create/complete`,
  fetchAvailableBankAccountsForContact: (contactId) => `${urlPrefix}/contact/${contactId}/bankaccount`,
  fetchTurnoverListForBankAccount: (contactId, options, accountType) => {
    const { offset, limit, searchTerm, filter } = options
    const { type, timePeriod, amount = {}, assignmentStates } = filter
    const includeInitialBooking = `includeInitialBooking=${[CASH_JOURNAL, CASH_JOURNAL_POS].includes(accountType)}`
    const offsetString = `&offset=${offset}`
    const limitString = `&limit=${limit}`
    const typeFilter = `&filter=${type}`
    const searchString = `&search=${encodeURIComponent(searchTerm)}`
    const timePeriodString = timePeriod?.dateTo && timePeriod?.dateFrom
      ? `&dateFrom=${timePeriod.dateFrom}&dateTo=${timePeriod.dateTo}`
      : ''
    const amountString = isNumber(amount.from) && isNumber(amount.to)
      ? `&amountFrom=${amount.from}&amountTo=${amount.to}`
      : ''
    const assignmentStatesString = assignmentStates ? `&assignmentStates=${encodeURIComponent(JSON.stringify(assignmentStates))}` : ''

    return `${urlPrefix}/bankaccount/${contactId}/turnover?${includeInitialBooking}${offsetString}${limitString}${typeFilter}${timePeriodString}${amountString}${assignmentStatesString}${searchString}`
  },
  fetchTransaction: `${urlPrefix}/transaction`,
  bookingProxy: `${config.resourceHost}booking-proxy`,
  deleteTransaction: (transactionId) => `${urlPrefix}/transaction/${transactionId}`,
  deleteTransactions: `${urlPrefix}/transaction`,
  fetchBankAccountList: `${urlPrefix}/bankaccount`,
  fetchSetupRequiredForAccount: (bankAccount) => {
    return `${urlPrefix}/contacts/${bankAccount.contactId}/setuprequired`
  },
  listSetting: `${config.resourceHost}setting/userlist/banking`,
  fetchLatestTurnoverForBankAccount: (bankAccountId) => `${urlPrefix}/bankaccount/${bankAccountId}/turnover/latest`,
  updateBankAccount: `${urlPrefix}/bankaccount`,
  updateBankAccountState: `${urlPrefix}/bankaccount/status`,
  deleteBankAccount: (bankAccountId) => `${urlPrefix}/contact/accounts/${bankAccountId}`,
  deleteBankContact: (bankContactId) => `${urlPrefix}/contacts/${bankContactId}`,
  findBank: (searchTerm) => `${urlPrefix}/bank/find/${searchTerm}`,
  bankContactPin: (contactId) => `${urlPrefix}/contact/${contactId}/pin`,
  autoSyncContact: (contactId) => `${urlPrefix}/contact/${contactId}/autosync`,
  getBankAccountDetails: (contactId) => `${urlPrefix}/contact/${contactId}`,
  filterTransactionUrl: `${config.resourceHost}transaction/filter/list`,
  addAssignmentsUrl: (bookingId) => `${urlPrefix}/booking/${bookingId}/assignments`,
  getBookingUrl: (id) => `${urlPrefix}/booking/${id}`,
  setCashJournalBalanceUrl: `${urlPrefix}/cashjournal/initialize`,
  createCashJournalBookingsForInvoicesUrl: `${urlPrefix}/cashjournal/invoices/assign`,
  createOfflineBookingsForInvoicesUrl: `${urlPrefix}/offline/invoices/assign`,
  syncBankAccountBalance: (bankAccountId) => `${urlPrefix}/bankaccount/${bankAccountId}/balance/sync`,
  privateBooking: `${urlPrefix}/cashJournal/privateBooking`,
  autoAssign: (bankAccountId) => `${urlPrefix}/bankaccount/${bankAccountId}/autoassign`, // TODO OW-3874: finalize after backend modifications
  getBankContactAccountDetails: (bankAccountId) => `${urlPrefix}/contacts/account/${bankAccountId}`,
  doAccountCheck: (bankAccountId) => `${urlPrefix}/bankaccount/${bankAccountId}/account-check`,
  doContactSync: (contactId) => `${urlPrefix}/contacts/${contactId}/sync`,
  getBankContactSettings: (contactId) => `${urlPrefix}/contacts/settings/${contactId}`,
  checkHasAssignments: (contactId) => `${urlPrefix}/contacts/${contactId}/has-assignments`,
}

export const privateBookingTypes = {
  DEPOSIT: 'privateDeposit',
  WITHDRAWAL: 'privateWithdrawal',
}

export const bookingAssignmentsSelectionFilterTypes = {
  PROPOSALS: 'proposals',
  ALL: 'all',
  INVOICE: 'invoice',
  CREDIT_NOTE: 'creditNote',
  EXPENSE: 'expense',
  REVENUE: 'revenue',
}

// add "sonstige Einnahmen"
export const bookingAssignmentsSelectionFilter = [
  { name: bookingAssignmentsSelectionFilterTypes.PROPOSALS, title: lang.bookingAssignment.selectionFilter.proposals, filter: bookingAssignmentsSelectionFilterTypes.PROPOSALS },
  { name: bookingAssignmentsSelectionFilterTypes.ALL, title: lang.bookingAssignment.selectionFilter.all, filter: bookingAssignmentsSelectionFilterTypes.ALL },
  { name: bookingAssignmentsSelectionFilterTypes.INVOICE, title: lang.bookingAssignment.selectionFilter.invoice, filter: bookingAssignmentsSelectionFilterTypes.INVOICE },
  { name: bookingAssignmentsSelectionFilterTypes.CREDIT_NOTE, title: lang.bookingAssignment.selectionFilter.creditNote, filter: bookingAssignmentsSelectionFilterTypes.CREDIT_NOTE },
  { name: bookingAssignmentsSelectionFilterTypes.EXPENSE, title: lang.bookingAssignment.selectionFilter.expense, filter: bookingAssignmentsSelectionFilterTypes.EXPENSE },
  { name: bookingAssignmentsSelectionFilterTypes.REVENUE, title: lang.bookingAssignment.selectionFilter.revenue, filter: bookingAssignmentsSelectionFilterTypes.REVENUE },
]

export const bookingAssignmentColumns = [
  {
    field: 'type',
    headerName: lang.bookingAssignment.columns.type,
    width: 100,
    tooltipField: 'type',
    valueFormatter: ({ value }) => {
      switch (value) {
        case TRANSACTION_TYPE_CASH_INVOICE:
        case TRANSACTION_TYPE_INVOICE:
        case TRANSACTION_TYPE_RECURRING_INVOICE:
        case TRANSACTION_TYPE_CLOSING_INVOICE:
        case TRANSACTION_TYPE_DEPOSIT_INVOICE: {
          return lang.bookingAssignment.columns.invoice
        }
        case TRANSACTION_TYPE_PARTIAL_INVOICE: {
          return lang.bookingAssignment.columns.partialInvoice
        }
        case TRANSACTION_TYPE_MANUAL_CREDIT_NOTE: {
          return lang.bookingAssignment.columns.manualCreditNote
        }
        case TRANSACTION_TYPE_SHOPIFY_CASH_INVOICE: {
          return lang.bookingAssignment.columns.shopifyCashInvoice
        }
        case TRANSACTION_TYPE_EXPENSE: {
          return lang.bookingAssignment.columns.expense
        }
        case TRANSACTION_TYPE_REVENUE: {
          return lang.bookingAssignment.columns.revenue
        }
        case TRANSACTION_TYPE_CASH_CANCELLATION:
        case TRANSACTION_TYPE_CANCELLATION: {
          return lang.bookingAssignment.columns.cancellation
        }
        case TRANSACTION_TYPE_CASH_CREDIT_NOTE:
        case TRANSACTION_TYPE_CREDIT_NOTE: {
          return lang.bookingAssignment.columns.creditNote
        }
      }
    },
  },
  {
    field: 'name',
    headerName: lang.bookingAssignment.columns.name,
    sort: 'desc',
    width: 100,
    tooltipField: 'name',
  },
  {
    field: 'date',
    headerName: lang.bookingAssignment.columns.date,
    sort: true,
    width: 75,
    tooltipField: 'date',
    comparator: dateFieldComparator,
    filter: 'agDateColumnFilter',
    filterParams: {
      comparator: dateFieldFilterComparator,
      filterOptions: ListDefaultSettings.DATE_FILTER_OPTIONS,
      inRangeInclusive: true,
    },
  },
  {
    field: 'number',
    headerName: lang.bookingAssignment.columns.number,
    sort: false,
    width: 80,
    tooltipField: 'number',
  },
  {
    field: 'outstandingAmount',
    headerName: lang.bookingAssignment.columns.outstandingAmount,
    sort: false,
    width: 80,
    tooltipField: 'outstandingAmount',
    headerComponentParams: { template: getCustomAmountHeaderTemplate() },
    cellRenderer: 'currencyAmountCellRenderer',
    filter: 'agNumberColumnFilter',
    filterParams: {
      inRangeInclusive: true,
    },
  },
].map((column) => ({
  ...column,
  suppressMovable: true,
  menuTabs: ['filterMenuTab'],
  headerTooltip: column.headerName,
}))

export const internalTransferColumns = [
  {
    field: 'bookingDate',
    headerName: lang.bookingAssignment.columns.bookingDate,
    sort: 'desc',
    valueFormatter: ({ value }) => {
      return moment(value).format(clientDateFormat)
    },
  },
  {
    field: 'bookingContactName',
    headerName: lang.bookingAssignment.columns.bookingContactName,
    sort: false,
  },
  {
    field: 'purposeDescription',
    headerName: lang.bookingAssignment.columns.purposeDescription,
    sort: false,
  },
  {
    field: 'bookingAmount',
    headerName: lang.bookingAssignment.columns.bookingAmount,
    sort: false,
    cellRenderer: 'currencyAmountCellRenderer',
  },
].map((column) => ({ ...column, menuTabs: [], filter: false, suppressMovable: true, width: 100 }))

export const filters = [
  {
    name: 'all',
    title: lang.label.allCapital,
    quickFilter: null,
  },
  {
    name: FINANCE_API.ASSIGNMENT_STATE.NOT_ASSIGNED,
    title: lang.booking.assignmentStates[FINANCE_API.ASSIGNMENT_STATE.NOT_ASSIGNED],
    quickFilter: {
      state: {
        type: 'set',
        values: [FINANCE_API.ASSIGNMENT_STATE.NOT_ASSIGNED],
      },
    },
  },
  {
    name: FINANCE_API.ASSIGNMENT_STATE.PARTIALLY_ASSIGNED,
    title: lang.booking.assignmentStates[FINANCE_API.ASSIGNMENT_STATE.PARTIALLY_ASSIGNED],
    quickFilter: {
      state: {
        type: 'set',
        values: [FINANCE_API.ASSIGNMENT_STATE.PARTIALLY_ASSIGNED],
      },
    },
  },
  {
    name: FINANCE_API.ASSIGNMENT_STATE.FULLY_ASSIGNED,
    title: lang.booking.assignmentStates[FINANCE_API.ASSIGNMENT_STATE.FULLY_ASSIGNED],
    quickFilter: {
      state: {
        type: 'set',
        values: [FINANCE_API.ASSIGNMENT_STATE.FULLY_ASSIGNED],
      },
    },
  },
]

export const referenceTypes = {
  openingBalance: lang.bankingCashJournalOpeningBalanceLabel,
}

export default {
  finApiDateFormat: 'YYYY-MM-DD',
  urls,
  privateBookingTypes,
  bookingAssignmentColumns,
  bookingAssignmentsSelectionFilter,
  bookingAssignmentsSelectionFilterTypes,
  internalTransferColumns,
  filters,
}
